import { useNotification } from '@/hooks/use-notification';
import type { NormalizedError } from '@/utils/error-utils';
import { useCallback } from 'react';

function buildErrorContent(
  error: NormalizedError,
  action: string,
): { title: string; description: string; id?: string } {
  switch (error.status) {
    case 401:
      return {
        id: 'session-expired',
        title: 'Session expired',
        description:
          'Your session may have expired. Please refresh and try again.',
      };
    case 403:
      return {
        title: 'Permission denied',
        description: 'You do not have permission to perform this action.',
      };
    case 404:
      return {
        title: 'Not found',
        description:
          'The resource could not be found. It may have been deleted.',
      };
    case 409:
      return {
        title: `Unable to ${action}`,
        description:
          error.message || 'A conflict occurred. Please refresh and try again.',
      };
    case 0:
      return {
        id: 'network-error',
        title: 'Network error',
        description: 'Check your connection and try again.',
      };
    default: {
      // Surface server validation field errors (400/422)
      if (error.kind === 'validation' && error.fieldErrors) {
        const fieldSummary = Object.entries(error.fieldErrors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join(' · ');
        return {
          title: `Failed to ${action}`,
          description: fieldSummary || error.message,
        };
      }
      return {
        title: `Failed to ${action}`,
        description:
          error.message || 'An unexpected error occurred. Please try again.',
      };
    }
  }
}

export const useErrorNotification = () => {
  const showNotification = useNotification();

  return useCallback(
    (error: NormalizedError, action: string) => {
      const { title, description, id } = buildErrorContent(error, action);
      showNotification({ type: 'error', title, description, id });
    },
    [showNotification],
  );
};
